import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { ArrowDownToLine, CirclePlus, Pencil, TrashBin } from '@gravity-ui/icons';
import { Button, Icon, Tooltip } from '@gravity-ui/uikit';
import Box from '@mui/material/Box';

import type {
	MRT_ColumnDef,
	MRT_PaginationState,
	MRT_Row,
	MRT_SortingState,
	MRT_TableInstance,
} from 'material-react-table';

import { useNavigateReplace } from 'hooks';
import { useDeleteBlankMutation, useLazyGetDownloadBlankQuery, useLazyGetDownloadPatientCardQuery } from 'store/api';

import { routes } from 'routes';

import { DataTable } from '../../ui-kit';
import { ADD_RATING, DELETE_BLANK, DOWNLOAD_BLANK, EDIT_BLANK, EXPORT_PDF } from '../constants';

import { DeleteBlankModal } from './delete-blank-modal';
import { format } from 'date-fns';
import styles from './styles.module.scss';
import BoxComponent from './components/BoxComponent';

interface DataProps {
	data: Person[];
	refetch: () => void;
	pageSize: number;
	setPageSize: React.Dispatch<React.SetStateAction<number>>;
	pageIndex: number;
	setPageIndex: React.Dispatch<React.SetStateAction<number>>;
	totalItems: number | undefined;
	sorting: MRT_SortingState;
	setSorting: React.Dispatch<React.SetStateAction<MRT_SortingState>>;
	role: string | undefined;
}

export interface Person {
	id: number | null | undefined;
	firstName: string;
	lastName: string;
	middleName?: string;
	establishment: {
		id?: number;
		name?: string | null;
	} | null;
	createdBlankAt: string | Date;
}


export const BlankTable = ({
	data,
	refetch,
	pageSize,
	setPageSize,
	pageIndex,
	setPageIndex,
	totalItems,
	sorting,
	setSorting,
	role,
}: DataProps) => {
	const columns = useMemo<MRT_ColumnDef<Person>[]>(
		() => [
			role === 'SPECIALIST'
				? {
						accessorFn: (row) =>
							`${row.lastName || ''} ${row.firstName || ''} ${row.middleName || ''}`.trim(),
						id: 'name',
						header: 'Имя клиента',
						enableHiding: false,
					}
				: {
						accessorKey: 'id',
						header: 'ID',
						enableHiding: false,
					},
			{
				accessorKey: 'establishment.name',
				header: 'Учреждение',
				enableHiding: false,
			},
			{
				accessorKey: 'createdBlankAt',
				header: 'Дата',
				size: 10,
				enableHiding: false,
				Cell: ({ cell }) => {
					const rawValue = cell.getValue<string>();
					if (!rawValue) return '';

					const formatted = format(new Date(rawValue), 'dd.MM.yyyy');

					return formatted;
				},
			},
		],
		[role],
	);

	const [pagination, setPagination] = useState<MRT_PaginationState>({
		pageIndex: pageIndex,
		pageSize: pageSize,
	});

	const [rowSelection, setRowSelection] = useState({});

	const navigate = useNavigateReplace();

	useEffect(() => {
		setPageSize(pagination.pageSize);
		setPageIndex(pagination.pageIndex);
	}, [pagination.pageSize, pagination.pageIndex, setPageSize, setPageIndex]);

	

	const RowAction = ({ row }: { row: MRT_Row<Person> }) => {
		const id = row.original.id;

		const [deleteBlank, { isLoading: isDeleting }] = useDeleteBlankMutation();
		const [openDeleteModal, setOpenDeleteModal] = useState(false);
		const [downloadPatientCard] = useLazyGetDownloadPatientCardQuery();

		const handleDelete = async () => {
			if (id !== null && id !== undefined) {
				try {
					await deleteBlank(id).unwrap();
					refetch();
					toast.success('Бланк успешно удалён');
				} catch (error) {
					console.error('Ошибка при удалении бланка:', error);
					toast.error('Ошибка при удалении бланка');
				}
			} else {
				console.error('Некорректный идентификатор бланка:', id);
			}
		};

		const handleDownload = async () => {
			if (id == null) {
				console.error('Некорректный идентификатор бланка:', id);
				return;
			}
			try {
				const blob = await downloadPatientCard(id).unwrap();
				const url = URL.createObjectURL(blob);
				const link = document.createElement('a');
				link.href = url;
				link.setAttribute('download', `${id}.pdf`);
				document.body.appendChild(link);
				link.click();
				link.remove();
			} catch (error) {
				console.error('Ошибка при скачивании бланка:', error);
				toast.error('Не удалось скачать бланк');
			}
		};

		return (
			<div className={styles.blankTable__action}>
				<Tooltip
					className={styles.blankTable__action__tooltip}
					content={ADD_RATING}
					placement="top"
					openDelay={500}
				>
					<Button
						view="normal-contrast"
						onClick={() => {
							navigate(
								row.original.id
									? routes.client_blank.getPath({
											clientId: row.original.id,
										})
									: '',
							);
						}}
						size="l"
					>
						<Icon data={CirclePlus} size={16} />
					</Button>
				</Tooltip>

				<Tooltip
					className={styles.blankTable__action__tooltip}
					content={EDIT_BLANK}
					placement="top"
					openDelay={500}
				>
					<Button
						view="flat"
						onClick={() => {
							navigate(
								row.original.id
									? routes.blank_id.getPath({
											clientId: row.original.id,
										})
									: '',
							);
						}}
						size="l"
					>
						<Icon data={Pencil} size={16} />
					</Button>
				</Tooltip>

				<Tooltip
					className={styles.blankTable__action__tooltip}
					content={DELETE_BLANK}
					placement="top"
					openDelay={500}
				>
					<Button view="flat" onClick={() => setOpenDeleteModal(true)} disabled={isDeleting} size="l">
						{isDeleting ? 'Удаление...' : <Icon data={TrashBin} size={16} />}
					</Button>
				</Tooltip>
				<DeleteBlankModal open={openDeleteModal} setOpen={setOpenDeleteModal} onConfirm={handleDelete} />
				<Tooltip
					className={styles.blankTable__action__tooltip}
					content={DOWNLOAD_BLANK}
					placement="top"
					openDelay={500}
				>
					<Button view="flat" size="l" onClick={handleDownload}>
						<Icon data={ArrowDownToLine} size={16} />
					</Button>
				</Tooltip>
			</div>
		);
	};

	return (
		<>
			<BoxComponent data={data} rowSelection={rowSelection} />
			<DataTable<Person>
				options={{
					columns,
					data,
					positionToolbarAlertBanner: 'none',
					renderRowActions: RowAction,
					manualPagination: true,
					manualSorting: true,
					rowCount: totalItems,
					state: { pagination, sorting, rowSelection },
					onPaginationChange: setPagination,
					onSortingChange: setSorting,
					onRowSelectionChange: setRowSelection,
					muiTableBodyRowProps: ({ row }) => ({
						onClick: (e: React.MouseEvent) => {
							const id = row.original.id;
							if (!id) return;
							const target = e.target as HTMLElement;
							// игнорируем клики по кнопкам, инпутам, ссылкам и элементам действий
							if (target.closest('button, [role="button"], input, a')) return;
							navigate(
								routes.client_blank.getPath({
									clientId: id,
								}),
							);
						},
						sx: { cursor: 'pointer' },
					}),
				}}
			/>
		</>
	);
};
